import { Card, CardContent, Stack, Typography, Chip, IconButton, Box, useTheme } from "@mui/material";
import { FoodsIcon, NumberWithUnit } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ActionGridData } from "./ActionsGrid";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";

type ActionCardProps = {
    action: ActionGridData;
    onEdit?: (actionId: string) => void;
    onDelete?: (actionId: string) => void;
};

export const ActionCard = (props: ActionCardProps) => {
    const { action, onEdit, onDelete } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { formatScore, getColor, selectedScorePrecision } = useEntityScores();

    const getStatusColors = (status: string): { backgroundColor: string; textColor: string } => {
        switch (status.toLowerCase()) {
            case "terminée":
            case "terminé":
                return {
                    backgroundColor: "#DFFFE6",
                    textColor: "#12A332",
                };
            case "en cours":
                return {
                    backgroundColor: "#CED4FF",
                    textColor: "#5268FF",
                };
            case "planifiée":
            case "planifié":
                return {
                    backgroundColor: "#FFEDCE",
                    textColor: "#FFB400",
                };
            case "en réflexion":
                return {
                    backgroundColor: "#FEEEEB",
                    textColor: "#FF7D60",
                };
            case "intégrée":
            case "intégré":
                return {
                    backgroundColor: "#FBE9FF",
                    textColor: "#B249C6",
                };
            default:
                return {
                    backgroundColor: "#F3F4F6",
                    textColor: "#6B7280",
                };
        }
    };

    return (
        <Card
            sx={{
                marginBottom: theme.spacing(2),
                borderRadius: "12px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                border: `1px solid ${theme.custom.grey[500]}`,
            }}
        >
            <CardContent
                sx={{
                    padding: theme.spacing(2),
                    "&:last-child": {
                        paddingBottom: theme.spacing(2),
                    },
                }}
            >
                {/* Header with title and status */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ marginBottom: theme.spacing(1.5) }}
                >
                    <Typography
                        variant="body"
                        sx={{
                            fontWeight: 700,
                            flex: 1,
                            marginRight: theme.spacing(1),
                            color: theme.palette.primary[700],
                        }}
                    >
                        {action.title}
                    </Typography>
                    <Chip
                        label={action.status}
                        size="small"
                        sx={{
                            backgroundColor: getStatusColors(action.status).backgroundColor,
                            color: getStatusColors(action.status).textColor,
                            fontSize: "0.75rem",
                            height: "24px",
                            fontWeight: 600,
                        }}
                    />
                </Stack>

                {/* Content rows */}
                <Stack spacing={1}>
                    {/* Categories */}
                    {action.categories && action.categories.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                            <Typography
                                variant="small"
                                sx={{
                                    color: theme.custom.grey[2000],
                                    minWidth: "80px",
                                    fontWeight: 600,
                                }}
                            >
                                {t("actionplan.categories", "Categories")}:
                            </Typography>
                            <Typography
                                variant="small"
                                sx={{
                                    color: theme.palette.text.primary,
                                    flex: 1,
                                }}
                            >
                                {action.categories.join(", ")}
                            </Typography>
                        </Box>
                    )}

                    {/* Period */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="small"
                            sx={{
                                color: theme.custom.grey[2000],
                                minWidth: "80px",
                                fontWeight: 600,
                            }}
                        >
                            {t("actionplan.period", "Period")}:
                        </Typography>
                        <Typography
                            variant="small"
                            sx={{
                                color: theme.palette.text.primary,
                            }}
                        >
                            {action.startYear || "-"} - {action.endYear || "-"}
                        </Typography>
                    </Box>

                    {/* Impact */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="small"
                            sx={{
                                color: theme.custom.grey[2000],
                                minWidth: "80px",
                                fontWeight: 600,
                            }}
                        >
                            {t("actionplan.impact", "Impact")}:
                        </Typography>
                        <ApproximateImpactTooltip
                            value={action.impact}
                            precision={selectedScorePrecision}
                            unit={action.unit}
                        >
                            <NumberWithUnit
                                value={formatScore(action.impact)}
                                unit={action.unit}
                                size={"small"}
                                colorOverride={getColor(action.impact)}
                            />
                        </ApproximateImpactTooltip>
                    </Box>
                </Stack>

                {/* Actions buttons */}
                {(action.canEdit || action.canDelete) && (
                    <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ marginTop: theme.spacing(1.5) }}>
                        {action.canEdit && onEdit && (
                            <IconButton
                                size="small"
                                onClick={() => onEdit(action.id)}
                                sx={{
                                    color: theme.palette.primary[600],
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary[50] || "rgba(59, 130, 246, 0.1)",
                                    },
                                }}
                            >
                                <FoodsIcon icon="edit" size={3} />
                            </IconButton>
                        )}
                        {action.canDelete && onDelete && (
                            <IconButton
                                size="small"
                                onClick={() => onDelete(action.id)}
                                sx={{
                                    color: theme.palette.error?.[600] || "#EF4444",
                                    "&:hover": {
                                        backgroundColor: theme.palette.error?.[50] || "rgba(239, 68, 68, 0.1)",
                                    },
                                }}
                            >
                                <FoodsIcon icon="delete" size={3} />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};
